import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { noop } from 'lodash';
import FilterHeader from '../../webcommon/header/filterheader/FilterHeader';
import Grid from '../../webcommon/grid/Grid';
import PropTypes from 'prop-types';
import moment from 'moment';
import { getFilters } from '../../webcommon/header/filterheader/selectors';
import ColumnSelector from '../../webcommon/columnselector/ColumnSelector';
import styles from '../csasummary/CsaSummary.module.scss';
import GridHeader from '../../common/gridheader/GridHeader';
import classNames from './util/classNames';
import {
    setCsaDrilldownColumns, csaDrilldownsLoaded,
    toggleRowExpanded, setAllExpanded, refreshData, csvExportEmpty, refreshCsaDrilldown
} from './actions';
import {
    combinedColumnsSelector, getDateFrom, getCsas
} from './selectors';
import gridStyle from './GridOverride.module.scss';
import ExportAsCsvButton from '../../common/gridheader/exportbutton/ExportAsCsvButton';
import DownloadCrif from '../../common/gridheader/downloadcrif/DownloadCrif';
import ExpanderButton from '../../common/gridheader/expanderbuttons/ExpanderButton';
import * as appConstants from '../../common/constants';
import { PAGE_REFRESH_RATE } from './constants';


const getRowLevelClassName = (rowInfo) => {
    const row = rowInfo.original;
    if (row.level === 0) { return classNames.levelZero; }
    return (rowInfo.index % 2 === 0)
        ? styles[classNames.even]
        : styles[classNames.odd];
};

const processDataForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const {
            imModel, silo, riskClass, senstivity, bucket, trades,
            imPledgeUsd, imReceiveUsd, imPledgeEur, imReceiveEur
        } = row;
        return {
            'IM Model': imModel,
            Silo: silo,
            'Risk Class': riskClass,
            Sensitivity: senstivity,
            Bucket: bucket,
            Trades: trades,
            'IM Pledge USD': imPledgeUsd,
            'IM Receive USD': imReceiveUsd,
            'IM Pledge EUR': imPledgeEur,
            'IM Receive EUR': imReceiveEur
        };
    };
    return rows.map(mapDataKeys);
};

const calculateCsvFilename = (date, csa) => `CSADrilldown${csa ? `_${csa}` : ''}_${(date ? moment(date) : moment()).format(appConstants.dateFormat)}.csv`;

class CsaDrilldown extends React.Component {
    constructor(props) {
        super(props);
        this.toggleExpanded = this.toggleExpanded.bind(this);
        this.setCsaDrilldownColumns = this.setCsaDrilldownColumns.bind(this);
        this.state = {
            allExpanded: true
        };
    }

    componentDidMount() {
        this.props.csaDrilldownsLoaded();
        const continuousDataLoad = setInterval(() => {
            this.props.refreshCsaDrilldown();
        }, PAGE_REFRESH_RATE);
        return () => clearInterval(continuousDataLoad);
    }

    setCsaDrilldownColumns(columns) {
        const data = columns.map((column) => {
            const { id, visible } = column;
            return { id, visible };
        });
        this.props.setCsaDrilldownColumns(data);
    }

    toggleExpanded() {
        this.setState((prevState) => {
            this.props.setAllExpanded(!prevState.allExpanded);
            return { allExpanded: !prevState.allExpanded };
        });
    }

    render() {
        return (
            <div className={ styles.csaSummary }>
                <FilterHeader
                    title="Tree View"
                    id="CSADrilldown"
                />
                <div className={ styles.gridHeaderContainer }>
                    <GridHeader
                        lastUpdated={ this.props.lastUpdated }
                        refreshControlsActive
                        refreshData={ this.props.refreshData }
                    >
                        <ExpanderButton
                            allExpanded={ this.state.allExpanded }
                            setAllExpanded={ this.toggleExpanded }
                        />
                        <div className={ styles.separator }>&nbsp;</div>
                        <DownloadCrif
                            csaSelected={ this.props.csas && this.props.csas.length > 0 ? this.props.csas[0] : '' }
                            dateFrom={ this.props.dateFrom ? moment(this.props.dateFrom).format(appConstants.dateFormat) : 'Error' }
                        />
                        <div className={ styles.separator }>&nbsp;</div>
                        <ColumnSelector
                            columns={ this.props.columns }
                            applyFilter={ this.setCsaDrilldownColumns }
                        />
                        <div className={ styles.separator }>&nbsp;</div>
                        <ExportAsCsvButton
                            csvData={ this.props.rows }
                            csvFilename={
                                calculateCsvFilename(this.props.dateFrom,
                                    this.props.csas.length > 0 ? this.props.csas[0] : null)
                            }
                            csvExportEmpty={ this.props.csvExportEmpty }
                            csvMapping={ processDataForCsvExport }
                        />
                    </GridHeader>
                </div>

                <div className={ styles.gridContainer }>
                    {this.props.columns.length
                        ? (
                            <Grid
                                rows={ this.props.rows }
                                columns={ this.props.columns.filter(
                                    column => column.visible
                                ) }
                                getRowLevelClassName={ getRowLevelClassName }
                                defaultColumnSpacing={ 2 }
                                loading={ this.props.loading }
                                onExpanderClick={ ({ id }) => {
                                    this.props.toggleRowExpanded(id);
                                } }
                                styles={ gridStyle }
                            />
                        )
                        : null}
                </div>
            </div>
        );
    }
}

CsaDrilldown.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string,
            type: PropTypes.string
        })
    ),
    csaDrilldownsLoaded: PropTypes.func,
    csvExportEmpty: PropTypes.func.isRequired,
    csas: PropTypes.arrayOf(PropTypes.string),
    dateFrom: PropTypes.string,
    lastUpdated: PropTypes.number,
    loading: PropTypes.bool,
    refreshData: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    setAllExpanded: PropTypes.func.isRequired,
    setCsaDrilldownColumns: PropTypes.func.isRequired,
    toggleRowExpanded: PropTypes.func.isRequired,
    location: PropTypes.shape({
        search: PropTypes.string
    }).isRequired
};

CsaDrilldown.defaultProps = {
    columns: [],
    csaDrilldownsLoaded: noop,
    csas: [],
    dateFrom: '',
    lastUpdated: moment().unix(),
    loading: false,
    rows: []
};

const mapStateToProps = state => ({
    columns: combinedColumnsSelector(state),
    csas: getCsas(getFilters(state, 'CSADrilldown')),
    dateFrom: getDateFrom(getFilters(state, 'CSADrilldown')),
    lastUpdated: state.csaDrilldown.lastUpdated,
    loading: state.csaDrilldown.loading,
    rows: state.csaDrilldown.csaDrilldownRows
});

const mapDispatchToProps = {
    csaDrilldownsLoaded,
    csvExportEmpty,
    refreshData,
    setAllExpanded,
    setCsaDrilldownColumns,
    toggleRowExpanded,
    refreshCsaDrilldown
};

export default withRouter(connect(mapStateToProps,
    mapDispatchToProps)(CsaDrilldown));

export { CsaDrilldown as CsaDrilldownUnconnected };
